/**
 * Discount Settings Tab Component
 * Phase 3 Frontend - Configure variable discount thresholds
 */

import { useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Stack,
  Grid,
  Switch,
  FormControlLabel,
  InputAdornment,
  CircularProgress,
  Alert,
  Typography,
  Divider,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AddIcon from '@mui/icons-material/Add';
import { useVariableDiscountSettings, useUpdateVariableDiscountSettings } from '../../../hooks/useDiscounts';
import { UpdateVariableDiscountSettingsInput } from '../../../types/discount.types';
import DiscountCoverageFields from '../discounts/DiscountCoverageFields';
import {
  COVERS_EVERYTHING,
  coverageFromSchoolDefault,
  coverageIsEmpty,
  normaliseCoverage,
  sameCoverage,
} from '../../../utils/discountCoverage';

export default function DiscountSettingsTab() {
  const { data: settings, isLoading, error } = useVariableDiscountSettings();
  const updateMutation = useUpdateVariableDiscountSettings();

  const { control, handleSubmit, reset, watch, formState: { isDirty } } = useForm<UpdateVariableDiscountSettingsInput>({
    defaultValues: {
      familyDiscountEnabled: false,
      familyDiscountPercent: 0,
      familyMinStudents: 2,
      instrumentDiscountEnabled: false,
      instrumentDiscountPercent: 0,
      instrumentMinCount: 2,
      programLevelDiscountEnabled: false,
      programLevelDiscountPercent: 0,
      programLevelMinCount: 2,
      defaultCoverage: { ...COVERS_EVERYTHING, coversCategoryIds: [] },
    },
  });

  // Update form when settings load
  useEffect(() => {
    if (settings) {
      reset({
        familyDiscountEnabled: settings.familyDiscountEnabled,
        familyDiscountPercent: settings.familyDiscountPercent,
        familyMinStudents: settings.familyMinStudents,
        instrumentDiscountEnabled: settings.instrumentDiscountEnabled,
        instrumentDiscountPercent: settings.instrumentDiscountPercent,
        instrumentMinCount: settings.instrumentMinCount,
        programLevelDiscountEnabled: settings.programLevelDiscountEnabled,
        programLevelDiscountPercent: settings.programLevelDiscountPercent,
        programLevelMinCount: settings.programLevelMinCount,
        defaultCoverage: coverageFromSchoolDefault(settings),
      });
    }
  }, [settings, reset]);

  const familyEnabled = watch('familyDiscountEnabled');
  const instrumentEnabled = watch('instrumentDiscountEnabled');
  const programLevelEnabled = watch('programLevelDiscountEnabled');

  const onSubmit = (data: UpdateVariableDiscountSettingsInput) => {
    // #625: the default is sent only when it changed (/qa phase 5 M1): an untouched save never
    // re-submits it (it may name a category deleted since).
    const { defaultCoverage, ...rest } = data;
    const changed =
      defaultCoverage && settings && !sameCoverage(defaultCoverage, coverageFromSchoolDefault(settings));
    updateMutation.mutate(changed ? { ...rest, defaultCoverage: normaliseCoverage(defaultCoverage) } : rest);
  };

  const handleReset = () => {
    if (settings) {
      reset({
        familyDiscountEnabled: settings.familyDiscountEnabled,
        familyDiscountPercent: settings.familyDiscountPercent,
        familyMinStudents: settings.familyMinStudents,
        instrumentDiscountEnabled: settings.instrumentDiscountEnabled,
        instrumentDiscountPercent: settings.instrumentDiscountPercent,
        instrumentMinCount: settings.instrumentMinCount,
        programLevelDiscountEnabled: settings.programLevelDiscountEnabled,
        programLevelDiscountPercent: settings.programLevelDiscountPercent,
        programLevelMinCount: settings.programLevelMinCount,
        defaultCoverage: coverageFromSchoolDefault(settings),
      });
    }
  };

  if (error) {
    return (
      <Alert severity="error">
        Failed to load discount settings. Please try again.
      </Alert>
    );
  }

  if (isLoading || !settings) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Variable Discounts Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure automatic discounts based on family size, instruments, and program level.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          component={Link}
          to="../custom-discounts"
        >
          Manage Custom Discounts
        </Button>
      </Box>

      {/* ======================================================================
          FAMILY DISCOUNT SECTION
          ====================================================================== */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Family Discount"
          subheader="Apply discount when family has multiple students"
          titleTypographyProps={{ variant: 'h6' }}
        />
        <Divider />
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Enable/Disable Toggle */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Controller
                    name="familyDiscountEnabled"
                    control={control}
                    render={({ field }) => <Switch {...field} checked={field.value} />}
                  />
                }
                label="Enable"
              />
            </Grid>

            {/* Percentage Input */}
            <Grid item xs={6} sm={2} md={1.5}>
              <Controller
                name="familyDiscountPercent"
                control={control}
                rules={{
                  min: { value: 0, message: 'Must be 0 or greater' },
                  max: { value: 100, message: 'Must be 100 or less' },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Discount %"
                    type="number"
                    size="small"
                    sx={{ maxWidth: 120 }}
                    disabled={!familyEnabled}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            {/* Minimum Students Input */}
            <Grid item xs={6} sm={2} md={1.5}>
              <Controller
                name="familyMinStudents"
                control={control}
                rules={{
                  min: { value: 2, message: 'Must be 2 or greater' },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Min Students"
                    type="number"
                    size="small"
                    sx={{ maxWidth: 120 }}
                    disabled={!familyEnabled}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ======================================================================
          INSTRUMENT DISCOUNT SECTION
          ====================================================================== */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Instrument Discount"
          subheader="Apply discount when student takes lessons on multiple instruments"
          titleTypographyProps={{ variant: 'h6' }}
        />
        <Divider />
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Enable/Disable Toggle */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Controller
                    name="instrumentDiscountEnabled"
                    control={control}
                    render={({ field }) => <Switch {...field} checked={field.value} />}
                  />
                }
                label="Enable"
              />
            </Grid>

            {/* Percentage Input */}
            <Grid item xs={6} sm={2} md={1.5}>
              <Controller
                name="instrumentDiscountPercent"
                control={control}
                rules={{
                  min: { value: 0, message: 'Must be 0 or greater' },
                  max: { value: 100, message: 'Must be 100 or less' },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Discount %"
                    type="number"
                    size="small"
                    sx={{ maxWidth: 120 }}
                    disabled={!instrumentEnabled}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            {/* Minimum Count Input */}
            <Grid item xs={6} sm={2} md={1.5}>
              <Controller
                name="instrumentMinCount"
                control={control}
                rules={{
                  min: { value: 2, message: 'Must be 2 or greater' },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Min Instruments"
                    type="number"
                    size="small"
                    sx={{ maxWidth: 120 }}
                    disabled={!instrumentEnabled}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ======================================================================
          PROGRAM LEVEL DISCOUNT SECTION
          ====================================================================== */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Program Level Discount"
          subheader="Apply discount when student takes lessons at multiple program levels"
          titleTypographyProps={{ variant: 'h6' }}
        />
        <Divider />
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Enable/Disable Toggle */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Controller
                    name="programLevelDiscountEnabled"
                    control={control}
                    render={({ field }) => <Switch {...field} checked={field.value} />}
                  />
                }
                label="Enable"
              />
            </Grid>

            {/* Percentage Input */}
            <Grid item xs={6} sm={2} md={1.5}>
              <Controller
                name="programLevelDiscountPercent"
                control={control}
                rules={{
                  min: { value: 0, message: 'Must be 0 or greater' },
                  max: { value: 100, message: 'Must be 100 or less' },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Discount %"
                    type="number"
                    size="small"
                    sx={{ maxWidth: 120 }}
                    disabled={!programLevelEnabled}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            {/* Minimum Count Input */}
            <Grid item xs={6} sm={2} md={1.5}>
              <Controller
                name="programLevelMinCount"
                control={control}
                rules={{
                  min: { value: 2, message: 'Must be 2 or greater' },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Min Levels"
                    type="number"
                    size="small"
                    sx={{ maxWidth: 120 }}
                    disabled={!programLevelEnabled}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ======================================================================
          WHAT DISCOUNTS COVER BY DEFAULT (#625 D6)
          ====================================================================== */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="What discounts cover by default"
          subheader="Used by the automatic discounts above, and pre-filled on a new discount"
          titleTypographyProps={{ variant: 'h6' }}
        />
        <Divider />
        <CardContent>
          <Controller
            name="defaultCoverage"
            control={control}
            rules={{
              validate: (v) => !v || !coverageIsEmpty(v) || 'Choose at least one kind of line the discount covers.',
            }}
            render={({ field }) => (
              <DiscountCoverageFields
                label="Discounts cover"
                value={field.value ?? { ...COVERS_EVERYTHING, coversCategoryIds: [] }}
                onChange={field.onChange}
              />
            )}
          />
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
            Changing this never changes an invoice already made: each discount keeps what it covered when it was
            applied.
          </Typography>
        </CardContent>
      </Card>

      {/* ======================================================================
          ACTION BUTTONS
          ====================================================================== */}
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={!isDirty || updateMutation.isPending}
          startIcon={updateMutation.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>

        <Button
          variant="outlined"
          onClick={handleReset}
          disabled={!isDirty}
          startIcon={<RestartAltIcon />}
        >
          Reset
        </Button>
      </Stack>
    </Box>
  );
}
